/**
 * Direct cache updates for the super-admin Organizations queries.
 * Every mutation the drawer runs uses these instead of
 * `invalidateQueries` so the drawer + table refresh without any
 * follow-up network round-trip.
 *
 * Both helpers hit `setQueriesData` with a prefix key match, so the
 * paginated `['orgs-admin', { includes: [...] }]` cache (an
 * `useInfiniteQuery` under the hood — shape `{ pages: [[...], ...] }`)
 * updates in place regardless of which include-permutation the
 * page mounted with.
 */

/**
 * Apply an updater to a specific org row inside the orgs-admin
 * infinite cache.
 * @param {QueryClient} queryClient
 * @param {string} orgId
 * @param {(org: object) => object} updater  Return the next org shape
 */
export const patchOrgInAdminCache = (queryClient, orgId, updater) => {
  queryClient.setQueriesData({ queryKey: ['orgs-admin'] }, (oldData) => {
    if (!oldData?.pages) return oldData;
    return {
      ...oldData,
      pages: oldData.pages.map((page) => (
        Array.isArray(page)
          ? page.map((org) => (
              org?.organization_id === orgId ? updater(org) : org
            ))
          : page
      )),
    };
  });
};

/**
 * Apply an updater to the `platform_fee_brackets` array of a
 * specific org in the orgs-admin cache. `updater` receives the
 * current array (may be undefined) and returns the next.
 */
export const patchOrgBracketsInAdminCache = (queryClient, orgId, updater) => {
  patchOrgInAdminCache(queryClient, orgId, (org) => ({
    ...org,
    platform_fee_brackets: updater(Array.isArray(org.platform_fee_brackets)
      ? org.platform_fee_brackets
      : []),
  }));
};

/**
 * Apply an updater to the `['org-fee-brackets', orgId]` cache the
 * FeesTab reads. Kept alongside the orgs-admin helper so a single
 * mutation can update both caches atomically.
 */
export const patchBracketsCache = (queryClient, orgId, updater) => {
  queryClient.setQueryData(['org-fee-brackets', orgId], (prev) => (
    updater(Array.isArray(prev) ? prev : [])
  ));
};
